import type { QuickFillItem } from '../../../shared/types';

export interface QuickFillSelectedCell {
  rowIndex: number;
  columnLabel?: string;
}

export interface QuickFillNavigationRequest {
  rowIndex: number;
}

export interface ViewportRange {
  startPosition: number | null;
  endPosition: number | null;
}

export interface ResolveQuickFillNavigationStateInput {
  selectedCells: QuickFillSelectedCell[];
  selectedRows: number[];
  navigationRequest: QuickFillNavigationRequest | null;
  quickFillData: QuickFillItem[];
  filteredRowPositionMap: Map<number, number>;
  navigationMarkerRows: number[];
  viewportRange: ViewportRange;
}

export interface QuickFillNavigationState {
  activeNavigationRowIndex: number | null;
  activeNavigationPosition: number | null;
  currentNavigationPosition: number | null;
  activeNavigationRowLabel: string | null;
  activeRowMarked: boolean;
  canJumpToActive: boolean;
  canToggleMarker: boolean;
}

export const buildFilteredRowPositionMap = (filteredRowIndices: number[]): Map<number, number> => {
  const map = new Map<number, number>();
  filteredRowIndices.forEach((rowIndex, position) => {
    map.set(rowIndex, position);
  });
  return map;
};

export const resolveQuickFillNavigationState = ({
  selectedCells,
  selectedRows,
  navigationRequest,
  quickFillData,
  filteredRowPositionMap,
  navigationMarkerRows,
  viewportRange,
}: ResolveQuickFillNavigationStateInput): QuickFillNavigationState => {
  const activeNavigationRowIndex =
    selectedCells.length > 0
      ? selectedCells[0]?.rowIndex ?? null
      : navigationRequest?.rowIndex != null
        ? navigationRequest.rowIndex
        : selectedRows.length > 0
          ? selectedRows[0]
          : null;
  const activeNavigationPosition =
    activeNavigationRowIndex == null
      ? null
      : filteredRowPositionMap.get(activeNavigationRowIndex) ?? null;
  const currentNavigationPosition =
    activeNavigationPosition != null && activeNavigationPosition >= 0
      ? activeNavigationPosition
      : viewportRange.startPosition != null && viewportRange.endPosition != null
        ? Math.round((viewportRange.startPosition + viewportRange.endPosition) / 2)
        : null;
  const activeNavigationRowLabel =
    activeNavigationRowIndex == null
      ? null
      : quickFillData[activeNavigationRowIndex]?.Name || `Row ${activeNavigationRowIndex + 1}`;

  return {
    activeNavigationRowIndex,
    activeNavigationPosition,
    currentNavigationPosition,
    activeNavigationRowLabel,
    activeRowMarked:
      activeNavigationRowIndex != null && navigationMarkerRows.includes(activeNavigationRowIndex),
    canJumpToActive:
      activeNavigationRowIndex != null && filteredRowPositionMap.has(activeNavigationRowIndex),
    canToggleMarker: activeNavigationRowIndex != null,
  };
};
